import {
  ApiError,
  projectApi,
  type CurrentUser,
  type WebsiteDiscovery,
} from '@shared/api-client';
import {
  discoveryJobActive,
  discoveryPollInterval,
} from '@shared/util/discovery-job';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

const storedId = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
export const useWebsiteDiscovery = (
  scope: string,
  onComplete?: (result: WebsiteDiscovery) => void,
) => {
  const client = useQueryClient();
  const user = client.getQueryData<CurrentUser>(['user']);
  const key = `website-discovery:${user?.id ?? 'anonymous'}:${user?.currentOrganization.id ?? 'none'}:${scope}`;
  const [saved, setSaved] = useState(() => ({ key, id: storedId(key) }));
  const id = saved.key === key ? saved.id : null;
  const generation = useRef(0);
  const activeKey = useRef(key);
  activeKey.current = key;
  const complete = useRef(onComplete);
  complete.current = onComplete;
  const handled = useRef(new Set<string>());
  const waiters = useRef(
    new Map<
      string,
      {
        resolve: (result: WebsiteDiscovery) => void;
        reject: (error: Error) => void;
      }[]
    >(),
  );
  const [visible, setVisible] = useState(() => !document.hidden);
  const remember = useCallback(
    (id: string | null) => {
      setSaved({ key, id });
      try {
        if (id) {
          sessionStorage.setItem(key, id);
        } else {
          sessionStorage.removeItem(key);
        }
      } catch {
        /* Optional resume storage. */
      }
    },
    [key],
  );
  useEffect(() => {
    setSaved({ key, id: storedId(key) });
  }, [key]);
  const query = useQuery({
    queryKey: ['website-discovery', key, id],
    queryFn: () => projectApi.getWebsiteDiscovery(id!),
    enabled: Boolean(id) && visible,
    refetchInterval: (state) => {
      if (
        !visible ||
        (state.state.error instanceof ApiError &&
          [403, 404].includes(state.state.error.status))
      ) {
        return false;
      }
      const job = state.state.data;
      return job && !discoveryJobActive(job)
        ? false
        : discoveryPollInterval(job?.createdAt ?? new Date().toISOString());
    },
    refetchOnWindowFocus: 'always',
    retry: (count, error) =>
      count < 1 &&
      !(error instanceof ApiError && [403, 404].includes(error.status)),
    staleTime: 0,
  });
  useEffect(() => {
    const changed = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', changed);
    return () => document.removeEventListener('visibilitychange', changed);
  }, []);
  const start = useMutation({ mutationFn: projectApi.startWebsiteDiscovery });
  const job = query.data;
  useEffect(() => {
    if (!job || discoveryJobActive(job)) {
      return;
    }
    const pending = waiters.current.get(job.id) ?? [];
    waiters.current.delete(job.id);
    if (job.status === 'completed' && job.result) {
      pending.forEach((waiter) => waiter.resolve(job.result!));
      if (!handled.current.has(job.id)) {
        handled.current.add(job.id);
        complete.current?.(job.result);
      }
    } else {
      pending.forEach((waiter) =>
        waiter.reject(new Error(job.error ?? 'Website analysis failed.')),
      );
    }
  }, [job]);
  useEffect(() => {
    if (
      id &&
      query.error instanceof ApiError &&
      [403, 404].includes(query.error.status)
    ) {
      (waiters.current.get(id) ?? []).forEach((waiter) =>
        waiter.reject(query.error!),
      );
      waiters.current.delete(id);
    }
  }, [id, query.error]);
  useEffect(
    () => () => {
      for (const pending of waiters.current.values()) {
        pending.forEach((waiter) =>
          waiter.reject(new Error('Analysis continues in the background.')),
        );
      }
      waiters.current.clear();
    },
    [],
  );
  const run = async (websiteUrl: string): Promise<WebsiteDiscovery> => {
    const version = ++generation.current;
    // A restored job may already have finished while the page was closed.
    // Resolve its first status read before deciding to enqueue another run.
    const restored =
      id && !job
        ? await client.fetchQuery({
            queryKey: ['website-discovery', key, id],
            queryFn: () => projectApi.getWebsiteDiscovery(id),
          })
        : null;
    if (version !== generation.current || key !== activeKey.current) {
      throw new Error('Website analysis was replaced.');
    }
    if (restored?.status === 'completed' && restored.result) {
      return restored.result;
    }
    const activeJob = job ?? restored;
    const next =
      activeJob &&
      discoveryJobActive(activeJob) &&
      activeJob.websiteUrl === websiteUrl
        ? activeJob
        : await start.mutateAsync(websiteUrl);
    if (version !== generation.current || key !== activeKey.current) {
      throw new Error('Website analysis was replaced.');
    }
    client.setQueryData(['website-discovery', key, next.id], next);
    remember(next.id);
    if (next.status === 'completed' && next.result) {
      return next.result;
    }
    return new Promise((resolve, reject) => {
      waiters.current.set(next.id, [
        ...(waiters.current.get(next.id) ?? []),
        { resolve, reject },
      ]);
    });
  };
  const error =
    start.error ??
    query.error ??
    (job?.status === 'failed'
      ? new Error(job.error ?? 'Website analysis failed.')
      : null);
  return {
    run,
    mutate: (websiteUrl: string) => {
      void run(websiteUrl).catch(() => undefined);
    },
    job,
    isPending:
      !(
        query.error instanceof ApiError &&
        [403, 404].includes(query.error.status)
      ) &&
      (start.isPending ||
        Boolean(id && !job && !query.isError) ||
        discoveryJobActive(job)),
    isError: Boolean(error),
    error,
    retryStatus: () => void query.refetch(),
    reset: () => {
      generation.current++;
      for (const pending of waiters.current.values()) {
        pending.forEach((waiter) =>
          waiter.reject(new Error('Website analysis was replaced.')),
        );
      }
      waiters.current.clear();
      remember(null);
      start.reset();
    },
  };
};
